import React from "react";
import { Link } from "react-router-dom";
import {
  FaUserCheck,
  FaMapMarkedAlt,
  FaShoppingCart,
  FaCreditCard,
  FaTruck,
  FaUndoAlt,
  FaBan,
  FaStore,
  FaMotorcycle,
  FaStar,
  FaHeadset,
  FaUserSlash,
  FaExclamationTriangle,
  FaSyncAlt,
} from "react-icons/fa";

import MyNavbar from "../../../components/general/navbar/MyNavbar";
import PageHero from "../../../components/general/pageHero/PageHero";
import Footer from "../../../components/general/footer/Footer";
import "../../../assets/css/customer/legal/TermsAndConditions.css";

const TermsAndConditions = () => {
  return (
    <div className="terms-page">
      <MyNavbar />
      <PageHero title="Terms & Conditions" breadcrumb="terms" />

      <div className="terms-content-wrapper">
        <p className="terms-updated-date">Last updated: September 2026</p>

        <p className="terms-intro">
          These Terms & Conditions ("Terms") govern the use of KisaanMart —
          an online marketplace connecting farmers and agri-shop sellers
          directly with customers, supported by an in-house delivery
          network. By creating an account or using KisaanMart as a
          Customer, Seller, or Deliveryman, you agree to be bound by these
          Terms. Please read them carefully.
        </p>

        <div className="terms-toc">
          <p className="terms-toc-title">On this page</p>
          <ol>
            <li><a href="#accounts">1. Accounts & Verification (KYC)</a></li>
            <li><a href="#browsing">2. Browsing, Cities & Bulk Orders</a></li>
            <li><a href="#orders">3. Placing an Order</a></li>
            <li><a href="#payments">4. Payments</a></li>
            <li><a href="#delivery">5. Delivery Process</a></li>
            <li><a href="#returns">6. Returns & Rejections at Delivery</a></li>
            <li><a href="#cancellations">7. Cancellations & Penalties</a></li>
            <li><a href="#sellers">8. Seller Obligations</a></li>
            <li><a href="#drivers">9. Deliveryman Obligations</a></li>
            <li><a href="#reviews">10. Reviews & Ratings</a></li>
            <li><a href="#support">11. Help & Support / Disputes</a></li>
            <li><a href="#suspension">12. Account Suspension & Termination</a></li>
            <li><a href="#liability">13. Limitation of Liability</a></li>
            <li><a href="#changes">14. Changes to These Terms</a></li>
          </ol>
        </div>

        {/* 1. Accounts */}
        <section id="accounts" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">1</span>
            <FaUserCheck /> Accounts & Verification (KYC)
          </h2>
          <div className="terms-section-body">
            <p>
              Anyone may register as a Customer, Seller, or Deliveryman.
              Customers register with an email and password; Sellers and
              Deliverymen must additionally complete a KYC (Know Your
              Customer) process, submitting identity documents (CNIC),
              business details, and — for Deliverymen — a profile photo,
              driving license, and vehicle documents.
            </p>
            <p>
              Sellers and Deliverymen may access their dashboards
              immediately after registering, but cannot list products or
              accept delivery jobs until their KYC submission is reviewed
              and approved by Admin. Admin may reject a KYC submission,
              request resubmission, or block a verified account at its
              discretion if information is found to be false, incomplete,
              or fraudulent.
            </p>
            <p>
              A customer may switch to a Seller account from their profile
              menu at any time; the same KYC requirement applies before
              they can list products.
            </p>
          </div>
        </section>

        {/* 2. Browsing / Cities / Bulk */}
        <section id="browsing" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">2</span>
            <FaMapMarkedAlt /> Browsing, Cities & Bulk Orders
          </h2>
          <div className="terms-section-body">
            <p>
              Customers may browse shops and products from any city on
              KisaanMart, regardless of their own registered city.
              Guests (users who are not logged in) may also browse freely
              but must log in to add items to a cart or wishlist.
            </p>
            <p className="terms-subheading">Same-city vs. intercity orders</p>
            <ul>
              <li>
                Orders where the seller and the delivery address are in the
                <strong> same city</strong> may be placed in any quantity.
              </li>
              <li>
                Orders where the seller and the delivery address are in
                <strong> different cities</strong> ("intercity" orders) are
                only permitted for bulk/wholesale quantities — each product
                has a seller-defined minimum quantity that must be met
                before an intercity order can proceed. This threshold is
                shown on the product page and enforced at checkout.
              </li>
              <li>
                A bulk discount (if set by the seller) is applied
                automatically whenever an order meets that same quantity
                threshold, whether the order is same-city or intercity.
              </li>
            </ul>
          </div>
        </section>

        {/* 3. Orders */}
        <section id="orders" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">3</span>
            <FaShoppingCart /> Placing an Order
          </h2>
          <div className="terms-section-body">
            <p>
              A cart may only contain products from a single seller at a
              time. To order from a different seller, the customer must
              complete or empty their existing cart first.
            </p>
            <p>
              After placing an order and entering a delivery address,
              available Deliverymen (matched by city/service area, vehicle
              capacity, and account standing) submit their own delivery
              charge quotes for that order. The customer reviews these
              quotes and selects a Deliveryman, or may cancel the order at
              this stage with no charge. Delivery charges are not fixed by
              KisaanMart and vary by Deliveryman.
            </p>
          </div>
        </section>

        {/* 4. Payments */}
        <section id="payments" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">4</span>
            <FaCreditCard /> Payments
          </h2>
          <div className="terms-section-body">
            <p>Two payment methods are available once a Deliveryman is selected:</p>
            <ul>
              <li>
                <strong>Card payment (Stripe):</strong> processed instantly.
                If payment fails, the order and any driver quotes are
                cancelled and the customer returns to their cart.
              </li>
              <li>
                <strong>Bank Transfer:</strong> the customer transfers the
                order amount to KisaanMart's bank account and uploads a
                receipt with the transferred amount. The order is held as
                "Pending Verification" — no stock is reserved and no
                Deliveryman is notified — until Admin verifies the
                transfer:
                <ul>
                  <li>If the amount matches or exceeds the order total, the order is confirmed.</li>
                  <li>If the amount transferred is less than the order total, the order is not created, and the customer may raise a refund request via Help & Support for the amount they sent.</li>
                  <li>If the customer transferred more than the order total, the excess may also be reclaimed via a Help & Support request.</li>
                </ul>
              </li>
            </ul>
            <p>
              The <strong>delivery charge</strong> quoted by the selected
              Deliveryman is collected separately, in cash, at the time of
              delivery ("Cash on Delivery") — it is not included in the
              online/bank-transfer payment for the products.
            </p>
          </div>
        </section>

        {/* 5. Delivery */}
        <section id="delivery" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">5</span>
            <FaTruck /> Delivery Process
          </h2>
          <div className="terms-section-body">
            <p>
              Once a Deliveryman is assigned and payment is confirmed, the
              Seller prepares the order and marks it "Ready for Pickup."
              The Deliveryman collects the order and, upon reaching the
              delivery address, marks the order as "Arrived" — at this
              point the order has not yet been unloaded or handed over.
            </p>
            <p>
              If the customer is satisfied, delivery is confirmed using a
              one-time code (OTP) shared by the customer and entered by
              the Deliveryman, after which the order is marked
              "Delivered." Once an order is marked Delivered, it cannot be
              returned or rejected — see Section 6 for the return window
              that applies before this point.
            </p>
          </div>
        </section>

        {/* 6. Returns */}
        <section id="returns" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">6</span>
            <FaUndoAlt /> Returns & Rejections at Delivery
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart's return window is at the point of delivery,
              before the order is unloaded or an OTP is confirmed — not
              afterward. If, upon inspecting the order, the customer finds
              a problem (damaged, incorrect, or otherwise unsatisfactory
              item), they may reject the order at that time by providing a
              reason and a photo through their account, while the
              Deliveryman is still present.
            </p>
            <div className="terms-highlight-box">
              <FaExclamationTriangle style={{ marginRight: 6 }} />
              Some products may be marked <strong>non-returnable</strong> by
              their Seller. If an order contains even one non-returnable
              item, the entire order cannot be rejected/returned at
              delivery.
            </div>
            <p>
              If an order is rejected at delivery: the product is returned
              to the Deliveryman's vehicle (it is never unloaded), any
              product payment already made is processed as a refund
              request, and stock is restored to the Seller. The customer
              is still responsible for paying the Deliveryman's quoted
              delivery charge in cash, since the delivery attempt was
              completed. The Seller separately compensates the Deliveryman,
              in cash, for returning the item — this return-leg payment is
              a matter between the Seller and Deliveryman and is not
              processed through the KisaanMart platform.
            </p>
          </div>
        </section>

        {/* 7. Cancellations */}
        <section id="cancellations" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">7</span>
            <FaBan /> Cancellations & Penalties
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart permits cancellations subject to the following
              rules and (where noted) penalties, which exist to discourage
              cancellations that waste another party's time or resources.
              Penalty amounts are set by KisaanMart and may be updated from
              time to time.
            </p>
            <p className="terms-subheading">Customers</p>
            <p>
              A customer may cancel an order any time before the Seller
              marks it "Ready for Pickup." Cancellation is not possible
              after this point. If the order had already been paid for, a
              cancellation fee (a percentage of the order value) is
              deducted from the refunded amount.
            </p>
            <p className="terms-subheading">Sellers</p>
            <p>
              A Seller who cancels an order after accepting it incurs a
              fixed cancellation penalty, deducted from their KisaanMart
              earnings.
            </p>
            <p className="terms-subheading">Deliverymen</p>
            <p>
              A Deliveryman who cancels a job after accepting it (before
              pickup) incurs a fixed cancellation penalty, deducted from
              their wallet balance.
            </p>
          </div>
        </section>

        {/* 8. Sellers */}
        <section id="sellers" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">8</span>
            <FaStore /> Seller Obligations
          </h2>
          <div className="terms-section-body">
            <ul>
              <li>Product listings must be accurate — including price, available stock, unit of measure, and whether the product is returnable.</li>
              <li>Orders must be packed and marked "Ready for Pickup" promptly and in the condition described in the listing.</li>
              <li>Sellers are responsible for confirming receipt of any returned item from a Deliveryman, including a photo showing its condition on arrival.</li>
              <li>Daily withdrawals from a Seller's wallet are subject to a KisaanMart-set limit; payout requests are reviewed and processed manually by Admin.</li>
              <li>Sellers may view but not reply to customer reviews left on their orders.</li>
            </ul>
          </div>
        </section>

        {/* 9. Drivers */}
        <section id="drivers" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">9</span>
            <FaMotorcycle /> Deliveryman Obligations
          </h2>
          <div className="terms-section-body">
            <p>
              Deliverymen declare their own current city and the list of
              cities they are willing to service; this information is
              self-reported and is relied upon in good faith to match jobs
              appropriately. Deliverymen must maintain a minimum wallet
              balance (topped up via bank transfer, subject to Admin
              approval) to remain eligible for new jobs, and may be
              assigned no more than two active jobs at a time.
            </p>
            <p>
              Vehicle capacity, as declared and verified during KYC,
              determines which orders a Deliveryman is eligible to bid on.
              Quoted delivery charges are set by the Deliveryman, not by
              KisaanMart.
            </p>
          </div>
        </section>

        {/* 10. Reviews */}
        <section id="reviews" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">10</span>
            <FaStar /> Reviews & Ratings
          </h2>
          <div className="terms-section-body">
            <p>
              Customers may leave a rating and review only after an order
              has been marked "Delivered." Reviews should reflect a
              genuine experience with the product, Seller, or delivery
              service and must not contain abusive, defamatory, or
              misleading content. KisaanMart reserves the right to remove
              reviews that violate this policy.
            </p>
          </div>
        </section>

        {/* 11. Support */}
        <section id="support" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">11</span>
            <FaHeadset /> Help & Support / Disputes
          </h2>
          <div className="terms-section-body">
            <p>
              Complaints related to a specific order (against a Seller or
              Deliveryman) are first raised as a direct conversation
              between the two parties involved. If unresolved, either
              party may escalate the matter to Admin, who will join the
              conversation and work to resolve it. General queries may be
              raised with Admin directly at any time.
            </p>
            <p>
              Refund requests — whether from a cancelled order, a rejected
              delivery, or a bank-transfer payment discrepancy — are
              submitted and tracked through this same Help & Support
              system, along with the bank details needed to process them.
            </p>
          </div>
        </section>

        {/* 12. Suspension */}
        <section id="suspension" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">12</span>
            <FaUserSlash /> Account Suspension & Termination
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart Admin may suspend or block any Customer, Seller,
              or Deliveryman account found to be in violation of these
              Terms, engaging in fraudulent activity, submitting false
              information, or subject to repeated, substantiated
              complaints. Sellers or Deliverymen who never complete their
              KYC submission may have their accounts removed. Suspended
              users may contact Help & Support to appeal a decision.
            </p>
          </div>
        </section>

        {/* 13. Liability */}
        <section id="liability" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">13</span>
            <FaExclamationTriangle /> Limitation of Liability
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart acts as a marketplace connecting independent
              Sellers, Customers, and Deliverymen, and is not itself the
              seller of any product listed on the platform nor the direct
              employer of any Deliveryman. While KisaanMart provides
              tools for verification, dispute resolution, and refund
              processing, it does not guarantee the quality, safety, or
              legality of items listed by Sellers, and its liability for
              any claim arising from a transaction is limited to the
              extent permitted by applicable law.
            </p>
          </div>
        </section>

        {/* 14. Changes */}
        <section id="changes" className="terms-section">
          <h2 className="terms-section-title">
            <span className="terms-section-number">14</span>
            <FaSyncAlt /> Changes to These Terms
          </h2>
          <div className="terms-section-body">
            <p>
              KisaanMart may update these Terms from time to time to
              reflect changes in its policies or operations. Continued use
              of the platform after an update constitutes acceptance of
              the revised Terms. Material changes will be highlighted on
              this page along with the date of the last update, shown at
              the top of this page.
            </p>
          </div>
        </section>

        <div className="terms-contact-box">
          Questions about these Terms? Reach out via our{" "}
          <Link to="/contact">Contact page</Link> or through Help & Support
          in your account.
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;
